import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useApprovalKeyboardShortcuts from './useApprovalKeyboardShortcuts';
import { NavigateFunction } from 'react-router-dom';
import {
  Flex,
  Button,
  Text,
  spaces,
  COUNTRIES,
  Country,
} from '@electricjs/arc';
import { UseFormReturn } from 'react-hook-form';
import { AIToolChip } from '@/components/AIChat/AIToolChip';
import { ApprovalButtons } from '@/components/AIChat/ApprovalButtons';
import { ToolDisplayStatus } from '@/pages/AIChatCopilotKit/types';
import {
  COPILOT_STATUS,
  CONFLICT_RESOLUTION,
  ConflictResolution,
} from '@/helpers/aiChat/tools';
import { CatalogCartEntry } from '@/types/storefront';
import { StorefrontCheckoutFormData } from '@/components/Storefront/components/StorefrontCheckoutFormProvider';
import { FirstStepFormData } from '@/components/Storefront/components/OtherCountryFlowProvider';
import { ROUTES, EMBEDDED_ROUTES } from '@/constants/routes';
import { getShoppingCountryOptions } from '@/components/Storefront/components/ShoppingCountrySelector';
import { useLazyGetEmployeeByIdQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { OTHER_SHOPPING_COUNTRY } from '@/redux/slices/storefrontContextSlice';

type AddToCartToolRendererProps = {
  args: Record<string, unknown>;
  status: string;
  respond?: (result: string) => void;
  navigate: NavigateFunction;
  catalogCart: CatalogCartEntry[];
  storefrontCheckoutForm?: UseFormReturn<StorefrontCheckoutFormData>;
  otherCountryFlowFirstStepForm?: UseFormReturn<FirstStepFormData>;
  handleAddToCart: (
    employeeId: string,
    productIds: string[],
    conflictResolution?: ConflictResolution,
    forceDomesticCountry?: Country
  ) => Promise<number | void>;
};

// Appended to every "successfully added" response so the AI doesn't turn a
// completed add-to-cart into an upsell pitch (e.g. proactively offering a
// warranty) — the user explicitly asked not to be offered anything further
// once they've already accepted an action.
const NO_UNSOLICITED_UPSELL_NOTE =
  ' Do not proactively suggest additional add-ons (like warranties or accessories) — only mention one if the user explicitly asks.';

/**
 * Validates arguments from CopilotKit tool args
 */
const validateArgs = (
  args: Record<string, unknown>
): {
  employeeId: string;
  productIds: string[];
} | null => {
  if (typeof args.employee_id !== 'string' || !args.employee_id) {
    return null;
  }

  if (!Array.isArray(args.product_ids)) {
    return null;
  }

  // Validate all product IDs are strings
  if (!args.product_ids.every(id => typeof id === 'string')) {
    return null;
  }

  return {
    employeeId: args.employee_id,
    productIds: args.product_ids,
  };
};

/**
 * Renders add to cart tool with approval UI, conflict resolution, and keyboard shortcuts
 */
export const AddToCartToolRenderer = ({
  args,
  status,
  respond,
  navigate,
  catalogCart,
  storefrontCheckoutForm,
  otherCountryFlowFirstStepForm,
  handleAddToCart,
}: AddToCartToolRendererProps) => {
  const { i18n } = useTranslation();
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const [wasRejected, setWasRejected] = useState(false);

  // Track previous args to detect new tool invocations
  const prevArgsRef = useRef<string>();

  // Validate args (must happen before hooks)
  const validatedArgs = validateArgs(args);
  const employeeId = validatedArgs?.employeeId;

  // Reset state when a new tool invocation is detected (args changed)
  useEffect(() => {
    const currentArgs = JSON.stringify(args);
    if (prevArgsRef.current && prevArgsRef.current !== currentArgs) {
      setWasRejected(false);
    }
    prevArgsRef.current = currentArgs;
  }, [args]);

  // Detect cart conflict: the cart is "in conflict" when it already has items
  // for a different employee than the one being requested. conflict_resolution
  // from args is intentionally not checked here — see useAddToCartTool.tsx for why
  // it is absent from the tool parameters.
  const currentEmployee = storefrontCheckoutForm?.getValues('employee');
  const hasCartItems = catalogCart.length > 0;
  const isEmployeeMismatch =
    currentEmployee?.value &&
    currentEmployee.value !== employeeId &&
    hasCartItems;
  const hasConflict = !!isEmployeeMismatch;

  // /embed/* routes are the only ones allowed while running inside an iframe
  // (see useIsEmbedded) — navigating to the plain ROUTES.GET_HARDWARE
  // path while embedded gets silently blocked, so the destination has to
  // match whichever context this tool is currently running in.
  const { isEmbedded } = useIsEmbedded();
  const purchaseHardwareRoute = isEmbedded
    ? EMBEDDED_ROUTES.GET_HARDWARE
    : ROUTES.GET_HARDWARE;

  // Detect a country mismatch: the requested employee has an address outside
  // the standard catalog countries. Like conflict resolution above, this is
  // never inferred from args — always resolved via an explicit button click.
  // When the employee isn't already the one in the form, their address isn't
  // known yet, so it's prefetched here — before any approval button renders —
  // so the country-choice buttons can show up on the very first tool call
  // instead of only after a blocked attempt.
  const { storefrontInternational } = useFlags();
  const organizationId = useGetOrganizationId();
  const isKnownEmployee = currentEmployee?.value === employeeId;
  const needsCountryLookup =
    storefrontInternational &&
    !hasConflict &&
    !!employeeId &&
    (validatedArgs?.productIds.length ?? 0) > 0 &&
    !isKnownEmployee;

  const [
    fetchEmployeeForCountryCheck,
    { data: fetchedEmployee, isFetching: isFetchingEmployeeCountry },
  ] = useLazyGetEmployeeByIdQuery();

  useEffect(() => {
    if (needsCountryLookup && employeeId) {
      fetchEmployeeForCountryCheck({
        employeeId,
        organizationId: organizationId ?? '',
      });
    }
  }, [
    needsCountryLookup,
    employeeId,
    organizationId,
    fetchEmployeeForCountryCheck,
  ]);

  const isResolvingCountry = needsCountryLookup && isFetchingEmployeeCountry;
  const targetEmployeeCountry = isKnownEmployee
    ? currentEmployee?.address?.country
    : fetchedEmployee?.address?.country;
  const hasCountryMismatch =
    !isResolvingCountry &&
    !hasConflict &&
    storefrontInternational &&
    !!targetEmployeeCountry &&
    (validatedArgs?.productIds.length ?? 0) > 0 &&
    !getShoppingCountryOptions().some(
      option => option.value === targetEmployeeCountry
    );

  // Conflict resolution always comes from an explicit button click — never inferred
  // from args or state. Each conflict button passes its resolution directly to
  // handleApprove, which calls handleAddToCart and then responds to CopilotKit.
  const handleApprove = useCallback(
    (explicitResolution?: ConflictResolution) => {
      if (!respond || !employeeId) return;

      const resolution = explicitResolution;

      // keep_cart reassigns only the employee — existing cart items are preserved
      // and no new products are added, so pass an empty array.
      const productIds =
        resolution === CONFLICT_RESOLUTION.KEEP_CART
          ? []
          : (validatedArgs?.productIds ?? []);

      // Handle navigate_to_cart case
      if (resolution === CONFLICT_RESOLUTION.NAVIGATE_TO_CART) {
        navigate(purchaseHardwareRoute);
        respond(
          'Navigated to the cart page for review. No products were added to the cart — the user chose to review it first before deciding.'
        );
        return;
      }

      // Execute cart operation
      handleAddToCart(employeeId, productIds, resolution)
        .then(addedCount => {
          if (productIds.length === 0) {
            // keep_cart: employee was reassigned, existing items preserved
            respond(
              `Successfully reassigned cart to the requested employee. Existing cart items were preserved.${NO_UNSOLICITED_UPSELL_NOTE}`
            );
          } else if (addedCount !== undefined) {
            if (resolution === CONFLICT_RESOLUTION.ADD_TO_EXISTING) {
              const ownerName =
                currentEmployee?.label ?? 'the existing employee';
              respond(
                `Successfully added ${addedCount} product${addedCount !== 1 ? 's' : ''} to cart. The cart belongs to ${ownerName} — products were added to their existing cart and the cart was not reassigned to the requested employee.${NO_UNSOLICITED_UPSELL_NOTE}`
              );
            } else if (resolution === CONFLICT_RESOLUTION.CLEAR_CART) {
              respond(
                `Successfully cleared cart and added ${addedCount} product${addedCount !== 1 ? 's' : ''} to cart${NO_UNSOLICITED_UPSELL_NOTE}`
              );
            } else {
              respond(
                `Successfully added ${addedCount} product${addedCount !== 1 ? 's' : ''} to cart${NO_UNSOLICITED_UPSELL_NOTE}`
              );
            }
          } else {
            respond(
              `Successfully added products to cart${NO_UNSOLICITED_UPSELL_NOTE}`
            );
          }
        })
        .catch((error: unknown) => {
          console.error('[AddToCartToolRenderer] Error:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to add products to cart';
          respond(`Error: ${message}`);
        });
    },
    [
      respond,
      employeeId,
      validatedArgs,
      navigate,
      purchaseHardwareRoute,
      handleAddToCart,
      currentEmployee?.label,
    ]
  );

  const handleReject = useCallback(() => {
    if (!respond) return;
    setWasRejected(true);
    respond('Add to cart cancelled by user');
  }, [respond]);

  // The concrete destination country always comes from this explicit button
  // click — one button per supported domestic option (see render below) —
  // see the forceDomesticCountry note in useAddToCartTool.tsx for why it's
  // never AI-supplied.
  const handleShipDomestically = useCallback(
    (countryCode: Country) => {
      if (!respond || !employeeId) return;

      const countryName = COUNTRIES[countryCode]?.name ?? countryCode;

      handleAddToCart(
        employeeId,
        validatedArgs?.productIds ?? [],
        undefined,
        countryCode
      )
        .then(addedCount => {
          // Navigate immediately, same as the international-flow button —
          // don't leave it to the AI to guess/ask whether the user wants to
          // see the cart, and say so explicitly in the response so it
          // doesn't offer to navigate again on top of this.
          navigate(purchaseHardwareRoute);
          respond(
            `Successfully added ${addedCount ?? 0} product${addedCount !== 1 ? 's' : ''} to cart for a domestic ${countryName} shipment, even though this employee's address is elsewhere, and navigated to the Storefront page to show the cart. Do not ask the user whether they want to be navigated there — it already happened.${NO_UNSOLICITED_UPSELL_NOTE}`
          );
        })
        .catch((error: unknown) => {
          console.error('[AddToCartToolRenderer] Error:', error);
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to add products to cart';
          respond(`Error: ${message}`);
        });
    },
    [
      respond,
      employeeId,
      validatedArgs,
      handleAddToCart,
      navigate,
      purchaseHardwareRoute,
    ]
  );

  // Sets shoppingCountry to "other" so the Storefront page renders the
  // international quote flow instead of the catalog, then navigates there —
  // the AI can't complete that flow itself since it needs manual recipient
  // details (national ID, address, phone, email) the user hasn't provided in
  // chat, and address validation the page already handles correctly.
  const handleUseInternationalFlow = useCallback(() => {
    if (!respond) return;

    const otherCountryOption = getShoppingCountryOptions().find(
      option => option.value === OTHER_SHOPPING_COUNTRY
    );
    if (otherCountryOption) {
      storefrontCheckoutForm?.setValue('shoppingCountry', otherCountryOption);
    }

    // Set the employee on the shared form too — this hand-off path doesn't
    // go through handleAddToCart (which normally does this), so without it
    // the Storefront page would open with "Search employee" empty even
    // though we already know who it's for.
    const employeeForPrefill = isKnownEmployee
      ? currentEmployee
      : fetchedEmployee
        ? transformEmployeesToOptions(
            [fetchedEmployee],
            i18n.language,
            isAdpWfnCanada
          )[0]
        : undefined;

    // Pre-fill whatever recipient contact details we already know from the
    // employee record, mirroring EmployeeSection.tsx's populateShippingDetails
    // (kept as a small self-contained copy here rather than a shared helper,
    // to avoid modifying that #3904 file). The recipient's national ID has
    // no employee-record equivalent and stays blank. This is a convenience
    // default only — everything remains fully editable on the page, and the
    // page's own address validation still applies before submission.
    if (employeeForPrefill) {
      storefrontCheckoutForm?.setValue('employee', employeeForPrefill);

      if (otherCountryFlowFirstStepForm) {
        otherCountryFlowFirstStepForm.setValue(
          'email',
          employeeForPrefill.personalEmail ?? employeeForPrefill.email ?? ''
        );
        otherCountryFlowFirstStepForm.setValue('phone', {
          phoneNumber: employeeForPrefill.phone ?? '',
        });
        if (employeeForPrefill.address) {
          otherCountryFlowFirstStepForm.setValue('address', {
            ...employeeForPrefill.address,
          });
        }
      }
    }

    navigate(purchaseHardwareRoute);
    respond(
      "Navigated to the Storefront page's international quote flow, with the employee's known email/phone/address pre-filled where available. The user needs to review and finish entering the recipient's details there manually — no products were added via chat."
    );
  }, [
    respond,
    storefrontCheckoutForm,
    otherCountryFlowFirstStepForm,
    isKnownEmployee,
    currentEmployee,
    fetchedEmployee,
    navigate,
    purchaseHardwareRoute,
    i18n.language,
    isAdpWfnCanada,
  ]);

  // Add keyboard shortcuts when awaiting approval (conflict/country-choice UI
  // excluded — user must click an explicit resolution button in those cases)
  useApprovalKeyboardShortcuts({
    onApprove: () => {
      if (!hasConflict && !hasCountryMismatch && !isResolvingCountry)
        handleApprove();
    },
    onDeny: handleReject,
    enabled: !!employeeId && status === COPILOT_STATUS.EXECUTING && !!respond,
  });

  // Report invalid args to CopilotKit as an effect — calling respond during
  // render would trigger a state update mid-render and cause React warnings
  const isInvalid = !validatedArgs || !employeeId;
  useEffect(() => {
    if (isInvalid && respond && status === COPILOT_STATUS.EXECUTING) {
      respond('Error: Invalid parameters for add to cart');
    }
  }, [isInvalid, respond, status]);

  // Conditional rendering after all hooks
  if (!validatedArgs || !employeeId) {
    // During inProgress, args stream in incrementally — show PENDING rather than
    // REJECTED until args are finalized, so the chip doesn't flash a failure state
    return (
      <AIToolChip
        id="add-to-cart-tool"
        toolSlug="add_products_to_cart"
        toolArgs={JSON.stringify(args)}
        variant={
          status === COPILOT_STATUS.EXECUTING
            ? ToolDisplayStatus.REJECTED
            : ToolDisplayStatus.PENDING
        }
      />
    );
  }

  // Map status to display status
  let displayStatus: ToolDisplayStatus;
  if (status === COPILOT_STATUS.EXECUTING) {
    displayStatus = ToolDisplayStatus.NEEDS_APPROVAL;
  } else if (status === COPILOT_STATUS.COMPLETE && wasRejected) {
    displayStatus = ToolDisplayStatus.REJECTED;
  } else if (status === COPILOT_STATUS.COMPLETE) {
    displayStatus = ToolDisplayStatus.COMPLETED;
  } else {
    displayStatus = ToolDisplayStatus.PENDING;
  }

  return (
    <Flex flexDirection="column" style={{ gap: spaces.space_xs_rem }}>
      <AIToolChip
        id="add-to-cart-tool"
        toolSlug="add_products_to_cart"
        toolArgs={JSON.stringify(args)}
        variant={displayStatus}
      />
      {status === COPILOT_STATUS.EXECUTING && respond && hasConflict && (
        <Flex flexDirection="column" style={{ gap: spaces.space_xs_rem }}>
          <Text variant="body">
            {currentEmployee?.label ?? 'Another employee'} has{' '}
            {catalogCart.length === 1
              ? '1 item'
              : `${catalogCart.length} items`}{' '}
            in their cart (
            {catalogCart
              .slice(0, 3)
              .map(e =>
                e.quantity > 1 ? `${e.item.name} ×${e.quantity}` : e.item.name
              )
              .join(', ')}
            {catalogCart.length > 3 && ` +${catalogCart.length - 3} more`}). How
            would you like to proceed?
          </Text>
          <Flex flexDirection="column" style={{ gap: spaces.space_2xs_rem }}>
            <Button
              id="conflict-add-to-existing"
              variant="outline"
              intent="neutral"
              buttonSize="small"
              onClick={() =>
                handleApprove(CONFLICT_RESOLUTION.ADD_TO_EXISTING)
              }>
              Add to existing cart
            </Button>
            <Button
              id="conflict-clear-cart"
              variant="outline"
              intent="neutral"
              buttonSize="small"
              onClick={() => handleApprove(CONFLICT_RESOLUTION.CLEAR_CART)}>
              Clear cart and add new products
            </Button>
            <Button
              id="conflict-keep-cart"
              variant="outline"
              intent="neutral"
              buttonSize="small"
              onClick={() => handleApprove(CONFLICT_RESOLUTION.KEEP_CART)}>
              Keep cart and reassign to new employee
            </Button>
            <Button
              id="conflict-navigate"
              variant="outline"
              intent="neutral"
              buttonSize="small"
              onClick={() =>
                handleApprove(CONFLICT_RESOLUTION.NAVIGATE_TO_CART)
              }>
              Review cart first
            </Button>
            <Button
              id="conflict-cancel"
              variant="outline"
              intent="neutral"
              buttonSize="small"
              onClick={handleReject}>
              Cancel
            </Button>
          </Flex>
        </Flex>
      )}
      {status === COPILOT_STATUS.EXECUTING &&
        respond &&
        !hasConflict &&
        isResolvingCountry && (
          <Text variant="body">
            Checking the employee&apos;s shipping address…
          </Text>
        )}
      {status === COPILOT_STATUS.EXECUTING &&
        respond &&
        !hasConflict &&
        !isResolvingCountry &&
        hasCountryMismatch && (
          <Flex flexDirection="column" style={{ gap: spaces.space_xs_rem }}>
            <Text variant="body">
              {currentEmployee?.label ?? 'This employee'}&apos;s address is in{' '}
              {COUNTRIES[targetEmployeeCountry as Country]?.name ??
                targetEmployeeCountry}
              , which isn&apos;t part of the standard US/Canada catalog. How
              would you like to proceed?
            </Text>
            <Flex flexDirection="column" style={{ gap: spaces.space_2xs_rem }}>
              {getShoppingCountryOptions()
                .filter(option => option.value !== OTHER_SHOPPING_COUNTRY)
                .map(option => (
                  <Button
                    key={option.value}
                    id={`country-ship-domestically-${option.value}`}
                    variant="outline"
                    intent="neutral"
                    buttonSize="small"
                    onClick={() =>
                      handleShipDomestically(option.value as Country)
                    }>
                    Ship to a{' '}
                    {COUNTRIES[option.value as Country]?.name ?? option.value}{' '}
                    address instead
                  </Button>
                ))}
              <Button
                id="country-use-international-flow"
                variant="outline"
                intent="neutral"
                buttonSize="small"
                onClick={handleUseInternationalFlow}>
                Use the international quote flow
              </Button>
              <Button
                id="country-cancel"
                variant="outline"
                intent="neutral"
                buttonSize="small"
                onClick={handleReject}>
                Cancel
              </Button>
            </Flex>
          </Flex>
        )}
      {status === COPILOT_STATUS.EXECUTING &&
        respond &&
        !hasConflict &&
        !isResolvingCountry &&
        !hasCountryMismatch && (
          <ApprovalButtons
            onApprove={() => handleApprove()}
            onReject={handleReject}
            style={{ flexDirection: 'row' }}
          />
        )}
    </Flex>
  );
};
